"use strict";
/*
 * Can consider shifting this logic into own "common" directory when we expand usage.
 * This can be considered to be general SQL / query management and not necessarily
 * tied to just DataLake / Athena purposes
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateQueryParamsAndGenerateSqlString = exports.validateForbiddenSqlString = exports.generateBindedSqlString = void 0;
const sqlstring_1 = __importDefault(require("sqlstring"));
const finley_types_1 = require("@finley-technologies/finley-types");
const error_1 = require("../error");
const utils_1 = require("../utils");
const generateSelectQueryString = (selectClause) => {
    if (typeof selectClause === 'string') {
        return selectClause;
    }
    const selectArray = [];
    for (const { fieldWithTableAlias, alias } of selectClause) {
        selectArray.push(`"${fieldWithTableAlias}" ${alias}`);
    }
    return selectArray.join(', ');
};
const generateTableQueryString = (tableClause) => {
    let tableQueryString = '';
    for (const { tableName, alias, join } of tableClause) {
        const baseTable = `${tableName} ${alias}`;
        if (join) {
            const { type, primaryIdWithTableAlias, foreignIdWithTableAlias } = join;
            tableQueryString += `${type.toUpperCase()} JOIN ${baseTable} ON "${primaryIdWithTableAlias}" = "${foreignIdWithTableAlias}" `;
        }
        else {
            tableQueryString += `${baseTable} `;
        }
    }
    return tableQueryString;
};
const generateBindedSqlString = (input, options = {}) => {
    var _a, _b;
    const { query, bindings } = input;
    const expectedNumOfBindings = (_b = (_a = query.match(/\?/g)) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
    (0, error_1.finleyAssert)(expectedNumOfBindings === bindings.length, `Expected ${bindings.length} bindings but saw ${expectedNumOfBindings} [query:${query}]`);
    return sqlstring_1.default.format(query, bindings, options.stringifyObjects);
};
exports.generateBindedSqlString = generateBindedSqlString;
const generateRawSqlQueryStringFromParameterized = (params) => {
    const { parameterizedSelectClause, parameterizedTableClause, parameterizedWhereClause, limitClause, } = params;
    let resultSqlString = '';
    const selectSqlString = generateSelectQueryString(parameterizedSelectClause);
    (0, error_1.finleyAssert)(selectSqlString, 'Require select input, received empty string');
    resultSqlString += `SELECT ${selectSqlString}`;
    const tableSqlString = generateTableQueryString(parameterizedTableClause);
    (0, error_1.finleyAssert)(tableSqlString, 'Require table input, received empty string');
    resultSqlString += ` FROM ${tableSqlString}`;
    if (parameterizedWhereClause) {
        const whereSqlString = (0, exports.generateBindedSqlString)({
            query: parameterizedWhereClause.sql,
            bindings: parameterizedWhereClause.params,
        }, { stringifyObjects: true });
        if (whereSqlString) {
            resultSqlString += ` WHERE ${whereSqlString}`;
        }
    }
    if (limitClause) {
        resultSqlString += ` LIMIT ${limitClause}`;
    }
    return (0, utils_1.toFullyTrimmedString)(resultSqlString);
};
const FORBIDDEN_SQL_KEYWORDS = [
    'CREATE TABLE',
    'ALTER TABLE',
    'UPDATE',
    'INSERT INTO',
    'DROP TABLE',
    'DELETE FROM',
];
const validateForbiddenSqlString = (sqlStringInput) => {
    const forbiddenKeywords = [];
    const upperCaseSqlString = (0, utils_1.toFullyTrimmedString)(sqlStringInput).toUpperCase();
    for (const keyword of FORBIDDEN_SQL_KEYWORDS) {
        if (upperCaseSqlString.match(`\\b${keyword}\\b`)) {
            forbiddenKeywords.push(keyword);
        }
    }
    if (forbiddenKeywords.length) {
        return {
            isValid: false,
            errorMessage: `Encountered following forbidden keywords: [${forbiddenKeywords.join(', ')}]`,
        };
    }
    return { isValid: true };
};
exports.validateForbiddenSqlString = validateForbiddenSqlString;
const validateSelectQuery = (selects) => {
    if (typeof selects === 'string') {
        return (0, exports.validateForbiddenSqlString)(selects);
    }
    if (selects.length === 0) {
        return {
            isValid: false,
            errorMessage: 'Received empty "SELECT" input',
        };
    }
    const uniqueFields = new Set();
    const uniqueAliases = new Set();
    for (const { fieldWithTableAlias, alias } of selects) {
        if (!fieldWithTableAlias) {
            return {
                isValid: false,
                errorMessage: 'Encountered empty "SELECT" field',
            };
        }
        if (uniqueFields.has(fieldWithTableAlias)) {
            return {
                isValid: false,
                errorMessage: `Encountered duplicate "SELECT" field: ${fieldWithTableAlias}`,
            };
        }
        uniqueFields.add(fieldWithTableAlias);
        if (alias && uniqueAliases.has(alias)) {
            return {
                isValid: false,
                errorMessage: `Encountered duplicate "SELECT" alias: ${alias}`,
            };
        }
        uniqueAliases.add(alias);
    }
    return { isValid: true };
};
const validateTableQuery = (tables) => {
    if (tables.length === 0) {
        return {
            isValid: false,
            errorMessage: 'Received empty "TABLE" input',
        };
    }
    let nonJoinTableCount = 0;
    const uniqueTableAliases = new Set();
    for (const { alias, join } of tables) {
        // Table clauses should only have 1 non-join and rest are join tables
        // The below would check non-join count and implicity check rest are join
        if (!join) {
            nonJoinTableCount += 1;
            if (nonJoinTableCount > 1) {
                return {
                    isValid: false,
                    errorMessage: 'Table clauses should only have 1 non-join parameter',
                };
            }
        }
        if (uniqueTableAliases.has(alias)) {
            return {
                isValid: false,
                errorMessage: `Encountered duplicate "TABLE" alias: ${alias}`,
            };
        }
        uniqueTableAliases.add(alias);
    }
    return { isValid: true };
};
const validateQueryParamsAndGenerateSqlString = (params) => {
    const typeCheck = finley_types_1.typeguards.datalake.isAthenaQueryParams(params);
    if (!typeCheck) {
        return {
            isValid: false,
            errorMessage: 'Failed "isAthenaQueryParams" typeguard check',
        };
    }
    const { parameterizedSelectClause, parameterizedTableClause } = params;
    const selectOutput = validateSelectQuery(parameterizedSelectClause);
    if (!selectOutput.isValid) {
        return { isValid: false, errorMessage: selectOutput.errorMessage };
    }
    const tableOutput = validateTableQuery(parameterizedTableClause);
    if (!tableOutput.isValid) {
        return { isValid: false, errorMessage: tableOutput.errorMessage };
    }
    const sqlStringOutput = generateRawSqlQueryStringFromParameterized(params);
    const forbiddenOutput = (0, exports.validateForbiddenSqlString)(sqlStringOutput);
    if (!forbiddenOutput.isValid) {
        return { isValid: false, errorMessage: forbiddenOutput.errorMessage };
    }
    return { isValid: true, params, sqlStringOutput };
};
exports.validateQueryParamsAndGenerateSqlString = validateQueryParamsAndGenerateSqlString;
//# sourceMappingURL=athena.utils.js.map